<script lang="ts" setup>
import type { VariantType } from '#components/elements/button';
import { BUTTON_BASE_ICON_SIZE } from '#components/elements/button';
import SpinnerLoader from '#components/elements/spinner/SpinnerLoader.vue';
import type { IconName } from '#components/icons/icon.model';
import PopOver from '#components/popover/PopOver.vue';
import type { SizeType } from '#components/utils/sizes.type';
import type { PropType } from 'vue';
import { computed } from 'vue';
import VueFeather from 'vue-feather';
import type { RouteLocationRaw } from 'vue-router';
import { RouterLink } from 'vue-router';

const props = defineProps({
  variant: { type: String as PropType<VariantType>, default: 'primary' },
  label: { type: String, required: false, default: undefined },
  size: { type: String as PropType<SizeType>, default: 'medium' },
  iconRight: { type: String as PropType<IconName>, required: false, default: undefined },
  iconLeft: { type: String as PropType<IconName>, required: false, default: undefined },
  disabled: { type: Boolean, default: false },
  background: {
    type: String as PropType<'transparent' | 'outline' | ''>,
    required: false,
    default: ''
  },
  fullWidth: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  to: {
    type: [Object, String] as PropType<RouteLocationRaw>,
    required: false,
    default: undefined
  },
  rounded: { type: Boolean, required: false, default: false },
  testId: { type: String, required: false, default: undefined },
  isExternalLink: { type: Boolean, required: false, default: false },
  popoverRightIcon: { type: String, required: false, default: undefined }
});
const emit = defineEmits<{
  click: [];
}>();

const classes = computed(() => ({
  'bg-primary hover:bg-primary-dark disabled:hover:bg-primary-dark active:bg-primary-darker! focus-visible:outline-primary':
    !props.background && props.variant === 'primary',
  'bg-white border border-default-very-light hover:bg-gray-50 disabled:hover:bg-white active:bg-gray-100':
    !props.background && (props.variant === 'secondary' || props.variant === 'default'),
  'bg-error hover:error-dark disabled:hover:bg-error active:bg-error-darker':
    !props.background && props.variant === 'error',
  'bg-success hover:success-dark disabled:hover:bg-success active:bg-success-darker':
    !props.background && props.variant === 'success',
  'bg-warning hover:bg-warning-dark disabled:hover:bg-warning active:bg-warning-darker focus-visible:outline-warning':
    !props.background && props.variant === 'warning',
  'text-primary': props.variant === 'secondary',
  'text-white': ['primary', 'error', 'success', 'warning'].includes(props.variant),
  'text-default': props.variant === 'default',
  'py-1 px-2 text-xs': props.size === 'small',
  'py-2 px-4 text-sm': props.size === 'medium',
  'py-3 px-8 text-md': props.size === 'large',
  'disabled:hover:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50': props.disabled,
  'bg-transparent hover:bg-gray-50 disabled:hover:bg-transparent  active:bg-gray-100 border-none p-2!':
    props.background === 'transparent',
  'bg-transparent border border-default-very-light': props.background === 'outline',
  'w-full': props.fullWidth,
  'rounded-full': props.rounded,
  'rounded-sm': !props.rounded
}));

const loaderClass = computed(() => ({
  'w-4 h-4': props.size === 'small',
  'w-5 h-5': props.size === 'medium',
  'w-6 h-6': props.size === 'large'
}));

const tag = computed(() => {
  if (props.to && !props.isExternalLink) {
    return RouterLink;
  }
  if (props.isExternalLink && props.to) {
    return 'a';
  }
  return 'button';
});
const buttonSize = computed(() => BUTTON_BASE_ICON_SIZE[props.size]);
</script>

<template>
  <Component
    :is="tag"
    :to="props.to"
    :href="props.isExternalLink ? props.to : undefined"
    :class="classes"
    class="inline-flex items-center focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 hover:cursor-pointer"
    :data-testid="testId"
    :target="props.isExternalLink ? '_blank' : undefined"
    :disabled="disabled"
    @click="emit('click')"
  >
    <div class="flex items-center w-full justify-center gap-x-2">
      <template v-if="isLoading">
        <div :class="loaderClass">
          <SpinnerLoader />
        </div>
      </template>
      <template v-else>
        <VueFeather v-if="iconLeft" :size="buttonSize" :type="iconLeft" aria-hidden="true" />

        <slot>
          <span v-if="label">{{ label }}</span>
        </slot>

        <!-- Duplicated in "InputSelectionOption.vue" -->
        <PopOver v-if="popoverRightIcon">
          <VueFeather
            class="cursor-pointer mt-1 text-black pointer-events-auto!"
            v-bind="{ type: 'info', size: buttonSize }"
            aria-hidden="true"
          />

          <template #content
            ><span class="text-white!">{{ popoverRightIcon }}</span></template
          >
        </PopOver>

        <VueFeather v-if="iconRight && !popoverRightIcon" :size="buttonSize" :type="iconRight" aria-hidden="true" />
      </template>
    </div>
  </Component>
</template>
